export type SearchOperationKind = "initial" | "load-more" | "preview";

export interface SearchOperationToken {
  readonly id: number;
  readonly generation: number;
  readonly kind: SearchOperationKind;
  readonly signal: AbortSignal;
}

interface ActiveOperation {
  token: SearchOperationToken;
  controller: AbortController;
}

export class SearchConcurrencyController {
  private readonly activeOperations = new Map<SearchOperationKind, ActiveOperation>();
  private currentGeneration = 0;
  private nextId = 1;
  private disposed = false;

  get generation(): number {
    return this.currentGeneration;
  }

  startInitial(): SearchOperationToken {
    this.assertActive();
    this.abortAll();
    this.currentGeneration += 1;
    return this.startOperation("initial");
  }

  startLoadMore(): SearchOperationToken {
    this.assertActive();
    return this.startOperation("load-more");
  }

  startPreview(): SearchOperationToken {
    this.assertActive();
    return this.startOperation("preview");
  }

  isCurrent(token: SearchOperationToken): boolean {
    if (this.disposed || token.signal.aborted || token.generation !== this.currentGeneration) {
      return false;
    }

    return this.activeOperations.get(token.kind)?.token.id === token.id;
  }

  finish(token: SearchOperationToken): boolean {
    if (!this.isCurrent(token)) return false;
    this.activeOperations.delete(token.kind);
    return true;
  }

  cancel(kind: SearchOperationKind): void {
    const operation = this.activeOperations.get(kind);
    if (operation === undefined) return;
    operation.controller.abort();
    this.activeOperations.delete(kind);
  }

  invalidateAll(): void {
    if (this.disposed) return;
    this.abortAll();
    this.currentGeneration += 1;
  }

  dispose(): void {
    if (this.disposed) return;
    this.abortAll();
    this.disposed = true;
  }

  private startOperation(kind: SearchOperationKind): SearchOperationToken {
    this.cancel(kind);
    const controller = new AbortController();
    const token: SearchOperationToken = {
      id: this.nextId,
      generation: this.currentGeneration,
      kind,
      signal: controller.signal,
    };
    this.nextId += 1;
    this.activeOperations.set(kind, { token, controller });
    return token;
  }

  private abortAll(): void {
    for (const operation of this.activeOperations.values()) {
      operation.controller.abort();
    }
    this.activeOperations.clear();
  }

  private assertActive(): void {
    if (this.disposed) {
      throw new Error("Concurrency controller is closed.");
    }
  }
}
